import { View } from 'foundations-components/components/View';
import styled from 'foundations-theming/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getDragDirection(start, endX, endY) {
  const swipeThresholdPx = 50;
  const deltaX = start.x - endX;
  const deltaY = start.y - endY;
  if (Math.abs(deltaX) <= Math.abs(deltaY)) return null;
  if (Math.abs(deltaX) < swipeThresholdPx) return null;
  return deltaX > 0 ? 'next' : 'prev';
}
function setInert(el) {
  if (el) {
    el.inert = true;
  }
}
const AUTO_SCROLL_DURATION_MS = 20000;
const RESUME_AUTO_SCROLL_DELAY_MS = 3000;
const Carousel = styled.div.withConfig({
  displayName: "ScrollableCarousel__Carousel"
})(({
  theme
}) => ({
  display: 'flex',
  overflowX: 'auto',
  // overflow-x: auto forces overflow-y to auto per CSS spec,
  // so add padding to prevent the BadgePill (top: -space[400]) from being clipped
  paddingBlock: theme.space['400'],
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing'
  },
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none'
  }
}));

// Each Group animates independently. translateX(-100%) moves exactly one group width
// since there is no gap between groups — spacing is handled inside each group via paddingInlineEnd.
const Group = styled.div.withConfig({
  displayName: "ScrollableCarousel__Group"
})(({
  theme,
  $isPaused
}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'start',
  flexShrink: 0,
  gap: theme.space['300'],
  // Trailing padding matches the inter-card gap so groups tile seamlessly
  paddingInlineEnd: theme.space['300'],
  willChange: 'transform',
  '@keyframes loop': {
    from: {
      transform: 'translateX(0)'
    },
    to: {
      transform: 'translateX(-100%)'
    }
  },
  animationName: 'loop',
  animationDuration: `${AUTO_SCROLL_DURATION_MS}ms`,
  animationTimingFunction: 'linear',
  animationIterationCount: 'infinite',
  animationPlayState: $isPaused ? 'paused' : 'running',
  '@media (prefers-reduced-motion: reduce)': {
    animationPlayState: 'paused'
  }
}));
const Card = styled.div.withConfig({
  displayName: "ScrollableCarousel__Card"
})(({
  theme
}) => {
  return {
    flexGrow: 1,
    blockSize: '100%',
    display: 'flex',
    alignItems: 'stretch',
    maxInlineSize: '400px',
    '@media (prefers-reduced-motion: reduce)': {
      marginInlineStart: theme.space['300']
    }
  };
});
const ScrollableCarousel = ({
  steps,
  onScroll
}) => {
  const dragStartRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const carouselRef = useRef(null);
  // Map from card element -> step index (covers all 3 group copies)
  const allCardRefsRef = useRef(new Map());
  const resumeTimerRef = useRef(null);
  const stepIndexRef = useRef(0);

  // Start scrolled to the middle group so user can swipe both ways
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const animationId = window.requestAnimationFrame(() => {
      el.scrollLeft = el.scrollWidth / 3;
    });
    return () => window.cancelAnimationFrame(animationId);
  }, [steps.length]);

  // Clear resume timer on unmount to avoid state update on unmounted component
  useEffect(() => {
    return () => {
      if (resumeTimerRef.current) {
        clearTimeout(resumeTimerRef.current);
      }
    };
  }, []);

  // Reset scroll position at boundaries for infinite user-swipe
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const handleScroll = () => {
      const groupWidth = el.scrollWidth / 3;
      if (el.scrollLeft >= groupWidth * 2) {
        el.scrollLeft -= groupWidth;
      } else if (el.scrollLeft <= groupWidth * 0.1) {
        el.scrollLeft += groupWidth;
      }
    };
    el.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);
  const pauseAutoScroll = useCallback(() => {
    setIsPaused(true);
    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current);
    }
  }, []);
  const resumeAutoScroll = useCallback(() => {
    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current);
    }
    resumeTimerRef.current = setTimeout(() => {
      setIsPaused(false);
    }, RESUME_AUTO_SCROLL_DELAY_MS);
  }, []);
  const handleDragEnd = useCallback((endX, endY) => {
    if (!dragStartRef.current) return;
    const direction = getDragDirection(dragStartRef.current, endX, endY);
    dragStartRef.current = null;
    if (!direction) return;

    // Animation is paused at this point (set in drag start), so
    // getBoundingClientRect accurately reflects where cards visually are.
    const el = carouselRef.current;
    if (!el) return;
    const containerCenter = el.getBoundingClientRect().left + el.offsetWidth / 2;
    let landingIndex = stepIndexRef.current;
    let closestDist = Infinity;
    allCardRefsRef.current.forEach((stepIdx, card) => {
      const rect = card.getBoundingClientRect();
      const cardCenter = rect.left + rect.width / 2;
      const dist = Math.abs(cardCenter - containerCenter);
      if (dist < closestDist) {
        closestDist = dist;
        landingIndex = stepIdx;
      }
    });
    onScroll === null || onScroll === void 0 || onScroll(landingIndex, direction);
    stepIndexRef.current = landingIndex;
  }, [onScroll]);
  const handleTouchStart = useCallback(e => {
    const touch = e.touches[0];
    dragStartRef.current = {
      x: touch.clientX,
      y: touch.clientY
    };
    pauseAutoScroll();
  }, [pauseAutoScroll]);
  const handleTouchEnd = useCallback(e => {
    resumeAutoScroll();
    const touch = e.changedTouches[0];
    handleDragEnd(touch.clientX, touch.clientY);
  }, [resumeAutoScroll, handleDragEnd]);
  const lastMouseXRef = useRef(null);
  const handleMouseDown = useCallback(e => {
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY
    };
    lastMouseXRef.current = e.clientX;
    pauseAutoScroll();
  }, [pauseAutoScroll]);
  const handleMouseMove = useCallback(e => {
    if (!dragStartRef.current || lastMouseXRef.current === null) return;
    const el = carouselRef.current;
    if (!el) return;
    const dx = lastMouseXRef.current - e.clientX;
    el.scrollLeft += dx;
    lastMouseXRef.current = e.clientX;
  }, []);
  const handleMouseUp = useCallback(e => {
    lastMouseXRef.current = null;
    resumeAutoScroll();
    handleDragEnd(e.clientX, e.clientY);
  }, [resumeAutoScroll, handleDragEnd]);
  const handleMouseLeave = useCallback(e => {
    if (!dragStartRef.current) return;
    lastMouseXRef.current = null;
    resumeAutoScroll();
    handleDragEnd(e.clientX, e.clientY);
  }, [resumeAutoScroll, handleDragEnd]);
  const renderCards = keyPrefix => steps.map((step, index) => /*#__PURE__*/_jsx(Card, {
    ref: el => {
      if (el) {
        allCardRefsRef.current.set(el, index);
      } else {
        allCardRefsRef.current.forEach((_stepIdx, card) => {
          // prevent stale detached nodes
          // e.g. user toggles off sales, steps won't contain prospecting but allCardRefsRef might still have a ref for it until the next render
          if (!card.isConnected) {
            allCardRefsRef.current.delete(card);
          }
        });
      }
    },
    children: step.content
  }, `${keyPrefix}-${step.id}`));
  return /*#__PURE__*/_jsx(View, {
    paddingBlock: "400",
    inlineSize: "100%",
    children: /*#__PURE__*/_jsxs(Carousel, {
      ref: carouselRef,
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd,
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseLeave,
      children: [/*#__PURE__*/_jsx(Group, {
        $isPaused: isPaused,
        "aria-hidden": "true",
        ref: setInert,
        children: renderCards('pre')
      }), /*#__PURE__*/_jsx(Group, {
        $isPaused: isPaused,
        children: renderCards('main')
      }), /*#__PURE__*/_jsx(Group, {
        $isPaused: isPaused,
        "aria-hidden": "true",
        ref: setInert,
        children: renderCards('post')
      })]
    })
  });
};
export default ScrollableCarousel;